import cloneDeep from 'lodash/cloneDeep';
import { deviceType } from 'react-device-detect';
import Cookies from 'universal-cookie';

import { User } from '@utils/backend-api/currentUser';
import { SubscriptionImpactEvent, SubscriptionResponse } from '@utils/backend-api/subscribe';
import { LEAD_CREATED_EVENT, LeadDetails } from '@utils/backend-api/users';
import { transformToSnakeCase } from '@utils/formatting';
import { GtagEvents } from '@utils/interfaces';
import { LeadType } from '@utils/types';

import { analyticsIdentify, analyticsTrack } from '../application/javascripts/analytics_util';

const USD_CURRENCY = 'USD';
const GOOGLE_ADS_EC_PURCHASE = 'AW-860455588/2gVkCMTqr6UZEKSFppoD';
const GOOGLE_ADS_EC_LEAD = 'AW-860455588/OTx8CPeor6UZEKSFppoD';
const PURCHASE_EVENT_COOKIE_NAME = 'purchaseEventFired';
const PURCHASE_EVENT_COOKIE_EXPIRATION_MINUTES = 120; // 2 hours
const SUBSCRIPTION_CONVERSION_EVENT_NAME = 'Subscription Conversion';

const IMPACT_TRACK_CONVERSION_EVENT_NAME = 'trackConversion';
const IMPACT_ONLNE_SALE_SITE_ID = 55633;

export enum Event {
  Click = 'click',
  Impression = 'impression',
  Lead = 'lead',
  Subscribe = 'subscribe',
  Typing = 'typing',
  Video = 'video',
}

export enum ContentType {
  Advertisement = 'advertisement',
  AiThread = 'ai_thread',
  AiMessageRun = 'ai_message_run',
  Article = 'article',
  Briefing = 'briefing',
  Comment = 'comment',
  ForumPost = 'forum_post',
  Employee = 'employee',
  Graphics = 'graphics',
  LandingPage = 'landing_page',
  Newsletter = 'newsletter',
  OrgChart = 'org_chart',
  PaymentMethod = 'payment_method',
  PersonalBriefing = 'personal_briefing',
  Project = 'project',
  User = 'user',
  TITVStream = 'titv_stream',
}

export enum Page {
  AboutPro = 'about_pro',
  Article = 'article',
  AsiaTechCoverage = 'asia_tech_coverage',
  BrandPartnerships = 'brand_partnerships',
  Briefing = 'briefing',
  Cancellation = 'cancellation',
  CommunityInsights = 'community_insights',
  DeepResearchSettings = 'deep_research_settings',
  DeepResearchThread = 'deep_research_thread',
  DeepResearchWelcome = 'deep_research_welcome',
  DiscountedUpgrade = 'discounted_upgrade',
  DualSubscribe = 'dual_subscribe',
  Forum = 'forum',
  FreeProTrial = 'free_pro_trial',
  GiftLandingForLead = 'gift_landing_for_lead',
  Graphic = 'graphic',
  GraphicsSearch = 'graphics_search',
  Homepage = 'homepage',
  Jobs = 'jobs',
  MultiOffersSubscribe = 'multi_offers_subscribe',
  Newsletter = 'newsletter',
  NewsletterFeed = 'newsletter_feed',
  NewsletterIndex = 'newsletter_index',
  NewsletterLanding = 'newsletter_landing',
  OrganizationAccount = 'organization_account',
  OrgChart = 'org_chart',
  OrgChartIndex = 'org_chart_index',
  ProCluster = 'pro_cluster',
  Project = 'project',
  Search = 'search',
  SingleOfferSubscribe = 'single_offer_subscribe',
  SubscribeNow = 'subscribe_now',
  SubscribeTest = 'subscribe_test',
  Team = 'team',
  TITV = 'titv',
  TITVEvent = 'titv_event',
  UpdatePage = 'update_page',
  Upgrade = 'upgrade',
  YoungProfessionals = 'young_professionals',
}

export enum ClickTarget {
  AcceptOffer = 'accept_offer',
  ActivateDeepResearch = 'activate_deep_research',
  AddNewPaymentMethod = 'add_new_payment_method',
  AddToTeam = 'add_to_team',
  AdvertisementSponsorLogo = 'advertisement_sponsor_logo',
  AnchorLink = 'anchor_link',
  AppDownloadApple = 'app_download_apple',
  AppDownloadGoogle = 'app_download_google',
  AppleSignIn = 'apple_sign_in',
  Archive = 'archive',
  ArticleCardCommenstCount = 'article_card_comments_count',
  ArticleImage = 'article_image',
  ArticlePickupTitle = 'article_pickup_title',
  Author = 'author',
  BackToForum = 'back_to_forum',
  BackToTop = 'back_to_top',
  Benefit = 'benefit',
  BrandPartnershipAd = 'brand_partnership_ad',
  BriefingSource = 'briefing_source',
  Button = 'button',
  Cancel = 'cancel',
  CancellationReason = 'cancellation_reason',
  Charts = 'charts',
  ClearSearch = 'clear_search',
  Clipper = 'clipper',
  Close = 'close',
  CollapseOAuthButtons = 'collapse_oauth_buttons',
  CommentLike = 'comment_like',
  CommentManage = 'comment_manage',
  CommentMessage = 'comment_message',
  CommentPost = 'comment_post',
  CommentReply = 'comment_reply',
  CommentReport = 'comment_report',
  CommentUnlike = 'comment_unlike',
  CommunityMenuItem = 'community_menu_item',
  ContactSales = 'contact_sales',
  Continue = 'continue',
  ContinueToCancel = 'continue_to_cancel',
  Copy = 'copy',
  CopyShareLink = 'copy_share_link',
  CorporateInquiry = 'corporate_inquiry',
  CorporateLeadCapture = 'corporate_lead_capture',
  CorporateSubscriptionAd = 'corporate_subscription_ad',
  CSVDownloadLink = 'csv_download_link',
  DeactivateDeepResearch = 'deactivate_deep_research',
  DeepResearch = 'deep_research',
  DeepResearchAd = 'deep_research_ad',
  DeepResearchBanner = 'deep_research_banner',
  DeepResearchIcon = 'deep_research_icon',
  DeepResearchLogo = 'deep_research_logo',
  DeepResearchScrollBanner = 'deep_research_scroll_banner',
  DeepResearchSubmit = 'deep_research_submit',
  DeepResearchUpgradeCTA = 'deep_research_upgrade_cta',
  Delete = 'delete',
  Directory = 'directory',
  DirectoryProfileInterest = 'directory_profile_interest',
  Downgrade = 'downgrade',
  DownloadApp = 'download_app',
  DownloadButton = 'download_button',
  DropDown = 'drop_down',
  Edit = 'edit',
  EmailSettings = 'email_settings',
  EventShowPageNavigation = 'EventShowPageNavigation',
  EventsMenuItem = 'events_menu_item',
  Excerpt = 'excerpt',
  ExpandButton = 'expand_button',
  ExpandOAuthButtons = 'expand_oauth_buttons',
  FeaturedArticleHeadline = 'featured_article_headline',
  FeaturedArticleImage = 'featured_article_image',
  FeaturedNewsletterName = 'featured_newsletter_name',
  FeaturedPartnerAd = 'featured_partner_ad',
  FeedBriefing = 'feed_briefing',
  Filter = 'filter',
  Finance = 'finance',
  FinePrintNavItem = 'fine_print_nav_item',
  Follow = 'follow',
  ForumHeadline = 'forum_headline',
  ForumPost = 'forum_post',
  GetStarted = 'get_started',
  GoogleSignIn = 'google_sign_in',
  GoToHomepage = 'go_to_homepage',
  GraphicClick = 'graphic_click',
  GraphicsSearchPageSearchSubmit = 'graphics_search_page_search_submit',
  GroupSubscription = 'group_subscription',
  Headline = 'headline',
  Highlight = 'highlight',
  JumpToBottom = 'jump_to_bottom',
  JumpToComments = 'jump_to_comments',
  KeepMySubscription = 'keep_my_subscription',
  LeadCapture = 'lead_capture',
  LearnMore = 'learn_more',
  Link = 'link',
  Logo = 'logo',
  MailTo = 'mail_to',
  MediaKit = 'media_kit',
  MenuClose = 'menu_close',
  MenuOpen = 'menu_open',
  MenuOption = 'menu_option',
  Minimize = 'minimize',
  MobileOffer = 'mobile_offer',
  MoreMenuItem = 'more_menu_item',
  NavLink = 'nav_link',
  NegativeFeedback = 'negative_feedback',
  NewQuery = 'new_query',
  NewsletterCard = 'newsletter_card',
  NewsletterToggle = 'newsletter_toggle',
  NextSlide = 'next_slide',
  Offer = 'offer',
  OnboardingComplete = 'onboarding_complete',
  Open = 'open',
  OrgChartCompanyReport = 'org_chart_company_report',
  OrgChartEmployeeTopLevel = 'org_chart_employee_top_level',
  OrgChartGridViewItem = 'org_chart_grid_view_item',
  OrgCharts = 'org_charts',
  PauseSubscription = 'pause_subscription',
  PersonalBriefingShare = 'personal_briefing_share',
  Pin = 'pin',
  PositiveFeedback = 'positive_feedback',
  PreviousSlide = 'previous_slide',
  ProfileAndArchive = 'profile_and_archive',
  ProMenuItem = 'pro_menu_item',
  ProPerk = 'pro_perk',
  QuickTag = 'quick_tag',
  ReadMore = 'read_more',
  RecommendedArticle = 'recommended_article',
  ReferAFriend = 'refer_a_friend',
  RegularSearchSubmit = 'regular_search_submit',
  RelatedEvent = 'related_event',
  RemoveCard = 'remove_card',
  Reporter = 'reporter',
  ResumeStandardSubscription = 'resume_standard_subscription',
  ResumePausedSubscription = 'resume_paused_subscription',
  Retry = 'retry',
  SaveTeamDomain = 'save_team_domain',
  SaveButton = 'save_button',
  ScoopsExternalLink = 'scoops_external_link',
  ScoopsReadMore = 'scoops_read_more',
  ScrollToTop = 'scroll_to_top',
  SearchButton = 'search_button',
  Send = 'send',
  SetAsPrimary = 'set_as_primary',
  SettingsMenuItem = 'settings_menu_item',
  ShareButton = 'share_button',
  ShareNewsletter = 'share_newsletter',
  ShowLess = 'show_less',
  ShowMoreComments = 'show_more_comments',
  SideNavItem = 'side_nav_item',
  SideNavToggle = 'side_nav_toggle',
  SignIn = 'sign_in',
  SocialNavItem = 'social_nav_item',
  SortColumn = 'sort_column',
  SortDropdown = 'sort_dropdown',
  StartTrial = 'start_trial',
  Step1Continue = 'step_1_continue',
  Step1Skip = 'step_1_skip',
  Step2Continue = 'step_2_continue',
  Step2Skip = 'step_2_skip',
  Step3Continue = 'step_3_continue',
  Step3Skip = 'step_3_skip',
  Step4Continue = 'step_4_continue',
  Step4Skip = 'step_4_skip',
  Step5Continue = 'step_5_continue',
  Stop = 'stop',
  SubmitButton = 'submit_button',
  Subscribe = 'subscribe',
  SubscribeAiAlertSubscription = 'subscribe_ai_alert_subscription',
  SubscribeCBPT = 'subscribe_cbpt',
  SubscribeNewsletter = 'subscribe_newsletter',
  SuggestedPrompt = 'suggested_prompt',
  Suggestion = 'suggestion',
  SuperPin = 'super_pin',
  Switch = 'switch',
  SwitchTable = 'switch_table',
  Tag = 'tag',
  Trial = 'trial',
  TeamLeadCapture = 'team_lead_capture',
  Tech = 'tech',
  ThreadNotification = 'thread_notification',
  Thread = 'thread',
  TI50Company = 'ti50_company',
  TITV = 'titv',
  TITVLatestEvent = 'titv_latest_event',
  TITVNextEvent = 'titv_next_event',
  TITVPastEvent = 'titv_past_event',
  Toggle = 'toggle',
  Twitter = 'twitter',
  Unfollow = 'unfollow',
  UnsubscribeAiAlertSubscription = 'unsubscribe_ai_alert_subscription',
  UnsubscribeNewsletter = 'unsubscribe_newsletter',
  Update = 'update',
  UpdatePaymentInformation = 'update_payment_information',
  UpdateProfileButton = 'update_profile_button',
  Upgrade = 'upgrade',
  UserRow = 'user_row',
  VeriffCancel = 'veriff_cancel',
  VeriffFinish = 'veriff_finish',
  VeriffOpen = 'veriff_open',
  VeriffReload = 'veriff_reload',
  VeriffStart = 'veriff_start',
  ViewAiThread = 'view_ai_thread',
  ViewAllArticles = 'view_all_articles',
  ViewAllNewsletters = 'view_all_newsletters',
  ViewList = 'view_list',
  ViewMore = 'view_more',
  ViewMyPost = 'view_my_post',
  ViewAINotificationSettings = 'view_ai_notification_settings',
  Weekend = 'weekend',
  WritePost = 'write_post',
}

export enum TrackingLocation {
  ActiveUsers = 'active_users',
  Admins = 'admins',
  AiAlertModal = 'ai_alert_modal',
  AiThreadModal = 'ai_thread_modal',
  Analytics = 'analytics',
  ArchivedUsers = 'archived_users',
  ArchivedStream = 'archived_stream',
  ArticlePickups = 'article_pickups',
  Awards = 'awards',
  AwarenessBar = 'awareness_bar',
  BestInTech = 'best_in_tech',
  BottomBanner = 'bottom_banner',
  BriefingSubscriptionUnit = 'briefing_subscription_unit',
  CancellationBenefits = 'cancellation_benefits',
  CancellationConfirmation = 'cancellation_confirmation',
  CancellationFeedback = 'cancellation_feedback',
  CancellationMismatch = 'cancellation_mismatch',
  CancellationOffer = 'cancellation_offer',
  CancellationQuality = 'cancellation_quality',
  CancellationTime = 'cancellation_time',
  CancellationPause = 'cancellation_pause',
  Channel = 'channel',
  ChatbotFeed = 'chatbot_feed',
  ChatbotFeedMenu = 'chatbot_feed_menu',
  ChatbotRightRail = 'chatbot_right_rail',
  Citations = 'citations',
  CommentReplySection = 'comment_reply_section',
  CommentSection = 'comment_section',
  CommunityCommentsUnit = 'community_comments_unit',
  ContentBody = 'content_body',
  ContinuedReading = 'continued_reading',
  CorporateLeadForm = 'corporate_lead_form',
  CorporateSubscription = 'corporate_subscription',
  Database = 'database',
  DeepResearchMidArticlePromo = 'deep_research_mid_article_promo',
  EditForumPost = 'edit_forum_post',
  EventsShowPage = 'EventsShowPage',
  Experts = 'experts',
  FeaturedNewsletters = 'featured_newsletters',
  FocusOnAI = 'focus_on_ai',
  Footer = 'footer',
  ForumHeader = 'forum_header',
  ForumUnit = 'forum_unit',
  Header = 'header',
  Help = 'help',
  HighlightedOffer = 'highlighted_offer',
  HorizontalOffer = 'horizontal_offer',
  InsightSection = 'insight_section',
  LandingPage = 'landing_page',
  LatestProArticles = 'latest_pro_articles',
  MainRiver = 'main_river',
  Modal = 'modal',
  MostPopularArticles = 'most_popular_articles',
  Nav = 'nav',
  NewslettersSignup = 'newsletters_signup',
  NewForumPost = 'new_forum_post',
  OfferCards = 'offer_cards',
  OfferList = 'offer_list',
  OfferTable = 'offer_table',
  OrgChartCompanyPage = 'org_chart_company_page',
  OrgChartCompanyReport = 'org_chart_company_report',
  OrgChartCompanyReportsSection = 'org_chart_company_reports_section',
  OrgChartEmployeeTopLevel = 'org_chart_employee_top_level',
  OrgChartGridViewItem = 'org_chart_grid_view_item',
  OrgChartHeaderPaywall = 'org_chart_header_paywall',
  OrgChartIndex = 'org_chart_index',
  OrgChartMoreOrgChartsGrid = 'org_chart_more_org_charts_grid',
  OrgChartSearchBar = 'org_chart_search_bar',
  PaymentForm = 'payment_form',
  Paywall = 'paywall',
  PaywallTwoOptionFullBleed = 'paywall_two_option_full_bleed',
  Post = 'post',
  ProHero = 'pro_hero',
  ProInsights = 'pro_insights',
  Quote = 'quote',
  RecommendedArticles = 'recommended_articles',
  RelatedEvent = 'related_event',
  RelatedStories = 'related_stories',
  RelatedStoriesMidArticle = 'related_stories_mid_article',
  RightRail = 'right_rail',
  ScoopsPageContent = 'scoops_page_content',
  ShareModal = 'share_modal',
  SideFeed = 'side_feed',
  SideNav = 'side_nav',
  SignupForm = 'subscribe_form',
  SlimNav = 'slim_nav',
  SubscribeToNewsletter = 'subscribe_to_newsletter',
  SuggestedPrompts = 'suggested_prompts',
  SwitchOffer = 'switch_offer',
  Takeaway = 'takeaway',
  TalentTracker = 'talent_tracker',
  TheInformationPro = 'the_information_pro',
  TI502025CategoryHeader = 'ti50_2025_category_header',
  TITVDescription = 'titv_description',
  TITVAdPartner = 'titv_ad_partner',
  TITVPopout = 'titv_popout',
  TopArticles = 'top_articles',
  TopArticlesSide = 'top_articles_side',
  UpcomingEvents = 'upcoming_events',
  VerticalOffer = 'vertical_offer',
  WeekendArticles = 'weekend_articles',
  WelcomeOnboarding = 'welcome_onboarding_modal',
}

export enum ImpressionElement {
  AIThreadDeletionModal = 'ai_thread_deletion_modal',
  AIAlertSubscriptionsExceededModal = 'ai_alert_subscriptions_exceeded_modal',
  Analytics = 'analytics',
  AwarenessBar = 'awareness_bar',
  AWSLogo = 'aws_logo',
  BrandPartnershipsAd = 'brand_partnerships_ad',
  Citations = 'citations',
  CorporateSubscriptionAd = 'corporate_subscription_ad',
  DeepLinkedComment = 'deep_linked_comment',
  DeepResearchAd = 'deep_research_ad',
  DeepResearchBanner = 'deep_research_banner',
  DeepResearchChatbot = 'deep_research_chatbot',
  DeepResearchJiggle = 'deep_research_jiggle',
  DeepResearchToggleModal = 'deep_research_toggle_modal',
  DeepResearchUnit = 'deep_research_unit',
  DiscountedUpgrade = 'discounted_upgrade',
  CommentsSection = 'comments_section',
  FeaturedPartnerAd = 'featured_partner_ad',
  Footer = 'footer',
  Modal = 'modal',
  MostPopularArticles = 'most_popular_articles',
  OrgChartCompanyReport = 'org_chart_company_report',
  OrgChartCompanyQuickFacts = 'org_chart_company_quick_facts',
  OrgChartReportsSection = 'org_chart_reports_section',
  OrgChartGridView = 'org_chart_grid_view',
  PageTemplates = 'page_templates',
  Payment = 'payment',
  PaywallBanner = 'paywall_banner',
  PaywallFull = 'paywall_full',
  PaywallFullBleed = 'paywall_full_bleed',
  Project = 'project',
  RelatedEvent = 'related_event',
  RelatedStories = 'related_stories',
  RelatedStory = 'related_story',
  ResumeSubscriptionModal = 'resume_subscription_modal',
  Takeaway = 'takeaway',
  TITVLiveStream = 'titv_live_stream',
  TITVMostRecentStream = 'titv_most_recent_stream',
  TITVPastEvent = 'titv_past_event',
  TITVPopout = 'titv_popout',
  Video = 'video',
}

export enum TypingInput {
  Comment = 'comment',
  CorporateLeadCapture = 'corporate_lead_capture',
  Email = 'email',
  FullName = 'full_name',
  LeadCapture = 'lead_capture',
  Reply = 'reply',
  SearchBar = 'search_bar',
  TeamLeadCapture = 'team_lead_capture',
}

export enum VideoPlayerEvent {
  Completed = 'completed',
  Paused = 'paused',
  Progress = 'progress',
  Started = 'started',
}

export enum GlobalModuleType {
  Modal = 'modal',
}

// Main list of event properties that are all optional to satisfy
// sometimes not passing all properties at the same time with useEventTracking
export interface EventProperties {
  accessLevelId?: number;
  clickTarget?: string;
  contentId?: number | string;
  contentIndex?: number;
  contentType?: ContentType;
  creatorIds?: number[];
  currentUser?: User;
  device?: string;
  eventName?: Event;
  impressionElement?: string;
  globalModuleId?: number | string;
  globalModuleType?: string;
  linkedAdScheduleId?: number;
  location?: string;
  page?: string;
  pageId?: number | string;
  subscriptionPlan?: string;
  subscriptionStatus?: string;
  tiEventLocation?: string;
  tiEventName?: string;
}

// This needs to extend to all of the possible properties. We can't use EventProperties
// because we need to make sure that we have context and omit currentUser.
interface SegmentEventProperties extends EventProperties {
  anonymousId?: string;
  context?: {
    identity_id?: number;
  };
}

interface ClickEventProperties {
  clickTarget: string;
  clickText?: string;
  clickLink?: string;
  contentId?: number;
  contentIndex?: number;
  contentType?: ContentType;
  creatorIds?: number[];
  offerCode?: string;
  payload?: object;
  tiEventName?: string;
  tiEventLocation?: string;
  upgradedFrom?: string;
  upgradedTo?: string;
  value?: string;
}

interface ImpressionProperties {
  context?: string;
  impressionElement: string;
  offerCode?: string;
}

interface TypingProperties {
  inputTarget: TypingInput;
}

interface VideoProperties {
  playedPercentage?: number;
  playerEvent?: VideoPlayerEvent;
  videoUrl?: string;
}

export function click(properties: ClickEventProperties) {
  return { ...properties, eventName: Event.Click };
}

export function impression(properties: ImpressionProperties) {
  return { ...properties, eventName: Event.Impression };
}

export function typing(properties: TypingProperties) {
  return { ...properties, eventName: Event.Typing };
}

export function video(properties: VideoProperties) {
  return { ...properties, eventName: Event.Video };
}

export function fireEvent(properties: EventProperties) {
  const [segmentEventProperties, context] = cloneAndSetPropertiesForSegment(properties);
  global?.analytics?.track(properties.eventName, segmentEventProperties, { context });
}

const cloneAndSetPropertiesForSegment = (
  properties: EventProperties
): [SegmentEventProperties, { identity_id: number }] => {
  const segmentProperties: SegmentEventProperties = cloneDeep(properties);
  delete segmentProperties.currentUser;

  segmentProperties.device = deviceType;
  segmentProperties.subscriptionStatus = properties.currentUser?.subscription_status;
  segmentProperties.accessLevelId = properties.currentUser?.access_level;

  const cookies = new Cookies();
  segmentProperties.anonymousId = cookies?.get('ajs_anonymous_id');
  const context = { identity_id: properties.currentUser?.identity_id };

  return [segmentProperties, context];
};

export function trackSubscriptionInGaAndAhoy({
  affiliation,
  email,
  initialSubscriptionPrice,
  offerCode,
  productSku,
  redirectTo,
  stripePlanName,
  subscriptionVariant,
  userId,
}: SubscriptionResponse) {
  const cookies = new Cookies();
  const isTracked = cookies?.get(PURCHASE_EVENT_COOKIE_NAME) === 'true';

  if (!isTracked) {
    window.createAhoyEvent(email);
    analyticsIdentify(userId.toString(), { call_location: 'tracking.ts' });
    analyticsTrack(SUBSCRIPTION_CONVERSION_EVENT_NAME, {
      email: email,
      currency: USD_CURRENCY,
      value: initialSubscriptionPrice,
    });
    const subscriptionGAEventProperties = {
      affiliation: affiliation,
      currency: USD_CURRENCY,
      email: email,
      items: [
        {
          brand: 'The Information',
          category: 'Subscriptions',
          coupon: offerCode,
          id: productSku, // Aligns with GA SKU
          name: stripePlanName, // Aligns with GA Product Name
          price: initialSubscriptionPrice,
          quantity: 1,
          variant: subscriptionVariant,
        },
      ],
      transaction_id: `${userId}_${Date.now()}`,
      user_id: userId,
      value: parseInt(initialSubscriptionPrice),
    };
    const conversionGAEventProperties = {
      send_to: GOOGLE_ADS_EC_PURCHASE,
      value: parseInt(initialSubscriptionPrice),
      currency: USD_CURRENCY,
      transaction_id: `${userId}_${Date.now()}_conversion`,
    };

    gtag('set', 'user_data', { email: email });
    gtag('event', GtagEvents.PURCHASE, subscriptionGAEventProperties);
    gtag('event', GtagEvents.CONVERSION, conversionGAEventProperties);
    triggerImpactSubscriptionConversion({
      email,
      initialSubscriptionPrice,
      newCustomer: true,
      offerCode,
      productSku,
      stripePlanName,
      userId,
    });
    cookies.set(PURCHASE_EVENT_COOKIE_NAME, 'true', {
      path: '/',
      maxAge: PURCHASE_EVENT_COOKIE_EXPIRATION_MINUTES * 60,
    });
  }

  window.location.href = redirectTo;
}

export const trackNewUser = (email: string, leadType: LeadType, leadDetails: LeadDetails) => {
  trackLeadInGa(email, leadType, leadDetails);
};

export function trackLeadInGa(email: string, leadType: LeadType, leadDetails: LeadDetails) {
  window.dataLayer = window.dataLayer || [];
  const gaLeadEventProperties = {
    event: LEAD_CREATED_EVENT,
    eventModel: {
      leadType: leadType,
      ...leadDetails,
    },
  };
  window.dataLayer.push(gaLeadEventProperties);
  if (typeof gtag !== 'undefined') {
    gtag('set', 'user_data', { email });
    gtag('event', GtagEvents.CONVERSION, { send_to: GOOGLE_ADS_EC_LEAD });
    gtag('event', GtagEvents.GENERATELEAD, {
      email,
      lead_type: leadType,
      ...leadDetails,
    });
  }
}

export function triggerImpactSubscriptionConversion({
  email,
  initialSubscriptionPrice,
  newCustomer = true,
  offerCode,
  productSku,
  stripePlanName,
  userId,
}: SubscriptionImpactEvent) {
  const cookies = new Cookies();
  const ajsAnonymousId = cookies?.get('ajs_anonymous_id');
  if (typeof window.ire !== 'function') {
    return;
  }

  const subscriptionImpactEventProperties = {
    orderId: `${userId}_${Date.now()}`,
    customProfileId: ajsAnonymousId,
    customerId: userId,
    customerEmail: email,
    customerStatus: newCustomer ? 'New' : 'Returning',
    currencyCode: USD_CURRENCY,
    orderPromoCode: offerCode || '',
    items: [
      {
        subTotal: Number(initialSubscriptionPrice).toFixed(2),
        category: 'Subscriptions',
        sku: productSku,
        quantity: 1,
        name: stripePlanName,
      },
    ],
  };

  window.ire(
    IMPACT_TRACK_CONVERSION_EVENT_NAME,
    IMPACT_ONLNE_SALE_SITE_ID,
    subscriptionImpactEventProperties,
    {
      verifySiteDefinitionMatch: true,
    }
  );
}
export function transformUnitNameToLocation(unitName: string) {
  return `${transformToSnakeCase(unitName)}_unit`;
}
